// src/services/billingAPI.js
// All API calls for billing system

const API = process.env.REACT_APP_API_URL;

// Get auth token from localStorage (works for both resident and admin)
const getAuthHeader = () => {
    const token = localStorage.getItem("token") || localStorage.getItem("residentToken");
    
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

/* ============================================================
   BILLING CONFIGURATION APIs
   ============================================================ */

// Get billing config for a tower
export const getBillingConfig = async (tower) => {
  try {
    const res = await fetch(`${API}/api/billing/config/${tower}`, {
      method: "GET",
      headers: getAuthHeader(),
    });

    if (!res.ok) {
      throw new Error("Failed to fetch billing config");
    }

    return await res.json();
  } catch (error) {
    console.error("Error fetching billing config:", error);
    throw error;
  }
};

// Create or update billing config
export const setBillingConfig = async (tower, config) => {
  try {
    const res = await fetch(`${API}/api/billing/config`, {
      method: "POST",
      headers: getAuthHeader(),
      body: JSON.stringify({
        tower,
        ...config,
      }),
    });

    if (!res.ok) {
      throw new Error("Failed to set billing config");
    }

    return await res.json();
  } catch (error) {
    console.error("Error setting billing config:", error);
    throw error;
  }
};

// Update discounts
export const updateDiscounts = async (tower, discounts) => {
  try {
    const res = await fetch(`${API}/api/billing/config/${tower}/discounts`, {
      method: "PATCH",
      headers: getAuthHeader(),
      body: JSON.stringify(discounts),
    });

    if (!res.ok) {
      throw new Error("Failed to update discounts");
    }

    return await res.json();
  } catch (error) {
    console.error("Error updating discounts:", error);
    throw error;
  }
};

// Update cost per sqft
export const updateCostPerSqft = async (tower, costPerSqft) => {
  try {
    const res = await fetch(`${API}/api/billing/config/${tower}/cost`, {
      method: "PATCH",
      headers: getAuthHeader(),
      body: JSON.stringify({ costPerSqft }),
    });

    if (!res.ok) {
      throw new Error("Failed to update cost");
    }

    return await res.json();
  } catch (error) {
    console.error("Error updating cost:", error);
    throw error;
  }
};

// Update late fee
export const updateLateFee = async (tower, lateFeePercentage, gracePeriodDays) => {
  try {
    const res = await fetch(`${API}/api/billing/config/${tower}/late-fee`, {
      method: "PATCH",
      headers: getAuthHeader(),
      body: JSON.stringify({ lateFeePercentage, gracePeriodDays }),
    });

    if (!res.ok) {
      throw new Error("Failed to update late fee");
    }

    return await res.json();
  } catch (error) {
    console.error("Error updating late fee:", error);
    throw error;
  }
};

// Get billing basis options
export const getBillingBasisOptions = async () => {
  try {
    const res = await fetch(`${API}/api/billing/billing-basis-options`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!res.ok) {
      throw new Error("Failed to fetch billing basis options");
    }

    return await res.json();
  } catch (error) {
    console.error("Error fetching billing basis options:", error);
    throw error;
  }
};

/* ============================================================
   CHARGES APIs
   ============================================================ */

// Generate charges for a tower
export const generateCharges = async (tower, frequency) => {
  try {
    const res = await fetch(`${API}/api/charges/generate/${tower}/${frequency}`, {
      method: "POST",
      headers: getAuthHeader(),
    });

    if (!res.ok) {
      throw new Error("Failed to generate charges");
    }

    return await res.json();
  } catch (error) {
    console.error("Error generating charges:", error);
    throw error;
  }
};

// Get charges for a resident
export const getResidentCharges = async (residentId) => {
  try {
    const res = await fetch(`${API}/api/charges/resident/${residentId}`, {
      method: "GET",
      headers: getAuthHeader(),
    });

    if (!res.ok) {
      throw new Error("Failed to fetch resident charges");
    }

    return await res.json();
  } catch (error) {
    console.error("Error fetching resident charges:", error);
    throw error;
  }
};

// Get all charges for a tower
export const getTowerCharges = async (tower, status = null) => {
  try {
    let url = `${API}/api/charges/tower/${tower}`;
    if (status) {
      url += `?status=${status}`;
    }

    const res = await fetch(url, {
      method: "GET",
      headers: getAuthHeader(),
    });

    if (!res.ok) {
      throw new Error("Failed to fetch tower charges");
    }

    return await res.json();
  } catch (error) {
    console.error("Error fetching tower charges:", error);
    throw error;
  }
};

// Get single charge
export const getCharge = async (chargeId) => {
  try {
    const res = await fetch(`${API}/api/charges/${chargeId}`, {
      method: "GET",
      headers: getAuthHeader(),
    });

    if (!res.ok) {
      throw new Error("Failed to fetch charge");
    }

    return await res.json();
  } catch (error) {
    console.error("Error fetching charge:", error);
    throw error;
  }
};

/* ============================================================
   PAYMENT APIs
   ============================================================ */

// Create Razorpay order
export const createPaymentOrder = async (chargeId, residentId) => {
  try {
    const res = await fetch(`${API}/api/payments/create-order`, {
      method: "POST",
      headers: getAuthHeader(),
      body: JSON.stringify({ chargeId, residentId }),
    });

    if (!res.ok) {
      throw new Error("Failed to create payment order");
    }

    return await res.json();
  } catch (error) {
    console.error("Error creating payment order:", error);
    throw error;
  }
};

// Verify payment
export const verifyPayment = async (
  razorpayOrderId,
  razorpayPaymentId,
  razorpaySignature,
  chargeId,
  residentId
) => {
  try {
    const res = await fetch(`${API}/api/payments/verify`, {
      method: "POST",
      headers: getAuthHeader(),
      body: JSON.stringify({
        razorpayOrderId,
        razorpayPaymentId,
        razorpaySignature,
        chargeId,
        residentId,
      }),
    });

    if (!res.ok) {
      throw new Error("Failed to verify payment");
    }

    return await res.json();
  } catch (error) {
    console.error("Error verifying payment:", error);
    throw error;
  }
};

// Get payment details
export const getPayment = async (paymentId) => {
  try {
    const res = await fetch(`${API}/api/payments/${paymentId}`, {
      method: "GET",
      headers: getAuthHeader(),
    });

    if (!res.ok) {
      throw new Error("Failed to fetch payment");
    }

    return await res.json();
  } catch (error) {
    console.error("Error fetching payment:", error);
    throw error;
  }
};

// Get resident payment history
export const getResidentPaymentHistory = async (residentId) => {
  try {
    const res = await fetch(`${API}/api/payments/resident/${residentId}`, {
      method: "GET",
      headers: getAuthHeader(),
    });

    if (!res.ok) {
      throw new Error("Failed to fetch payment history");
    }

    return await res.json();
  } catch (error) {
    console.error("Error fetching payment history:", error);
    throw error;
  }
};

// Get tower payment summary
export const getTowerPaymentSummary = async (tower) => {
  try {
    const res = await fetch(`${API}/api/payments/tower/${tower}/summary`, {
      method: "GET",
      headers: getAuthHeader(),
    });

    if (!res.ok) {
      throw new Error("Failed to fetch payment summary");
    }

    return await res.json();
  } catch (error) {
    console.error("Error fetching payment summary:", error);
    throw error;
  }
};